/**
 * My Training inbox query keys (Step 24).
 *
 * Exported for cross-feature invalidation — the curriculum/program
 * assign mutations invalidate ``myTrainingKeys.all`` so recipients'
 * inboxes refresh.
 */

export const MY_TRAINING_ROOT_KEY = 'my-training' as const;

/**
 * Backend-supported inbox sort modes — only ones that affect the
 * server response. Client-side sorts (assigned/status) reuse the same
 * cache entry, so we key on the backend sort only.
 */
export type InboxBackendSortKey = 'due_date_asc' | 'due_date_desc';

export const myTrainingKeys = {
  all: [MY_TRAINING_ROOT_KEY] as const,
  inbox: (userId: string, sort: InboxBackendSortKey = 'due_date_asc') =>
    [MY_TRAINING_ROOT_KEY, 'inbox', userId, sort] as const,
  /**
   * Pending-secondary-attestations inbox (F-1 follow-up).
   *
   * Caller-scoped: the backend reads ``principal.user_id`` directly,
   * but we still key on ``userId`` so cache entries don't leak across
   * sign-out / sign-in transitions.
   */
  pendingAttestations: (userId: string) =>
    [MY_TRAINING_ROOT_KEY, 'pending-attestations', userId] as const,
  /**
   * Pending-instructor-signoffs inbox — sibling of
   * ``pendingAttestations`` for INSTRUCTOR_SIGNOFF items in PENDING.
   * Caller-scoped, same userId-keyed pattern.
   */
  pendingInstructorSignoffs: (userId: string) =>
    [MY_TRAINING_ROOT_KEY, 'pending-instructor-signoffs', userId] as const,
  /**
   * Historical sign-offs the caller has performed — backs the
   * trainee Approvals → Completed pane.
   */
  completedSignoffs: (userId: string) =>
    [MY_TRAINING_ROOT_KEY, 'completed-signoffs', userId] as const,
};
